import { getBasicContentSchema } from '@/lib/zy-tiptap-utils';
import { Mark, Node, mergeAttributes, type JSONContent } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { ReactNodeViewRenderer } from '@tiptap/react';
import DetectAnswerExplanationNodeView from './detect-answer-explanation-node-view';
import DetectAnswerOptionNodeView from './detect-answer-option-node-view';
import DetectAnswerQuestionNodeView from './detect-answer-question-node-view';

const detectAnswerOptionSyncPluginKey = new PluginKey('detectAnswerOptionSync');

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        detectAnswer: {
            insertDetectAnswer: (options?: InsertDetectAnswerOptions) => ReturnType;
            insertBareDetectAnswer: (options?: InsertDetectAnswerOptions) => ReturnType;
            toggleDetectAnswerOptionFromSelection: (questionPos: number) => ReturnType;
            removeDetectAnswerOption: (optionId: string) => ReturnType;
        };
    }
}

export interface InsertDetectAnswerOptions {
    instruction?: string;
    question?: string;
    title?: string;
}

function createOptionId() {
    return `da-${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 8)}`;
}

const defaultDetectAnswerNode = (options: InsertDetectAnswerOptions = {}) => {
    return {
        type: 'detectAnswer',
        attrs: {
            title: options.title || 'Detect Answer Question',
        },
        content: [
            {
                type: 'detectAnswerQuestion',
                content: [
                    {
                        type: 'paragraph',
                        content: options.question ? [{ type: 'text', text: options.question }] : undefined,
                    },
                ],
            },
        ],
    };
};

function getDetectAnswerMarkIdsInRange(state: any, from: number, to: number): Set<string> {
    const ids = new Set<string>();
    state.doc.nodesBetween(from, to, (node: PMNode) => {
        if (!node.isText || !node.marks) {
            return;
        }
        node.marks.forEach(mark => {
            if (mark.type.name === 'detectAnswerOptionMark' && mark.attrs.optionId) {
                ids.add(mark.attrs.optionId);
            }
        });
    });
    return ids;
}

function findAncestorNodePosition(state: any, pos: number, nodeTypeName: string): number {
    const $pos = state.doc.resolve(pos);
    for (let depth = $pos.depth; depth >= 0; depth--) {
        const node = $pos.node(depth);
        if (node.type.name === nodeTypeName) {
            return $pos.before(depth);
        }
    }
    return -1;
}

function findOptionPositionsForQuestion(detectAnswerNode: PMNode, detectAnswerPos: number): number[] {
    const positions: number[] = [];
    let offset = detectAnswerPos + 1;
    detectAnswerNode.forEach(child => {
        if (child.type.name === 'detectAnswerOption') {
            positions.push(offset);
        }
        offset += child.nodeSize;
    });
    return positions;
}

function removeDetectAnswerOptionFromQuestion(
    tr: any,
    state: any,
    optionId: string,
    detectAnswerNode: PMNode,
    detectAnswerPos: number,
    detectMarkType: any,
): boolean {
    let changed = false;

    state.doc.descendants((node: PMNode, pos: number) => {
        if (!node.isText || !node.marks?.length) {
            return;
        }

        const hasMark = node.marks.some(mark => mark.type.name === 'detectAnswerOptionMark' && mark.attrs.optionId === optionId);
        if (!hasMark) {
            return;
        }

        tr.removeMark(pos, pos + node.nodeSize, detectMarkType);
        changed = true;
    });

    const optionPositions = findOptionPositionsForQuestion(detectAnswerNode, detectAnswerPos);
    for (let i = optionPositions.length - 1; i >= 0; i--) {
        const pos = optionPositions[i];
        const optionNode = tr.doc.nodeAt(pos);
        if (optionNode?.type.name === 'detectAnswerOption' && optionNode.attrs.optionId === optionId) {
            tr.delete(pos, pos + optionNode.nodeSize);
            changed = true;
        }
    }

    return changed;
}

function getQuestionNodeInfo(detectAnswerNode: PMNode, detectAnswerPos: number): { questionNode: PMNode; questionPos: number } | null {
    let offset = detectAnswerPos + 1;

    for (let i = 0; i < detectAnswerNode.childCount; i++) {
        const child = detectAnswerNode.child(i);
        if (child.type.name === 'detectAnswerQuestion') {
            return {
                questionNode: child,
                questionPos: offset,
            };
        }
        offset += child.nodeSize;
    }

    return null;
}

function getMarkedOptionsInQuestion(questionNode: PMNode, questionPos: number): Array<{ optionId: string; text: string }> {
    const orderedOptionIds: string[] = [];
    const optionTextParts = new Map<string, string[]>();
    const optionLastEndPos = new Map<string, number>();

    questionNode.descendants((node, pos) => {
        const text = node.text;

        if (!node.isText || typeof text !== 'string' || !node.marks?.length) {
            return;
        }

        const absPos = questionPos + 1 + pos;
        const absEnd = absPos + text.length;
        node.marks.forEach(mark => {
            if (mark.type.name !== 'detectAnswerOptionMark' || !mark.attrs.optionId) {
                return;
            }

            const optionId = mark.attrs.optionId;
            if (!optionTextParts.has(optionId)) {
                optionTextParts.set(optionId, []);
                orderedOptionIds.push(optionId);
            }

            const textParts = optionTextParts.get(optionId);
            const lastEndPos = optionLastEndPos.get(optionId);

            if (textParts && lastEndPos != null && absPos > lastEndPos + 1) {
                textParts.push('\n');
            }

            textParts?.push(text);
            optionLastEndPos.set(optionId, absEnd);
        });
    });

    return orderedOptionIds.map(optionId => ({
        optionId,
        text: (optionTextParts.get(optionId) || []).join('').trim(),
    }));
}

function getSyncedDetectAnswerNode(detectAnswerNode: PMNode, detectAnswerPos: number): PMNode | null {
    const questionInfo = getQuestionNodeInfo(detectAnswerNode, detectAnswerPos);
    if (!questionInfo) {
        return null;
    }

    const optionNodes: PMNode[] = [];
    detectAnswerNode.forEach(child => {
        if (child.type.name === 'detectAnswerOption') {
            optionNodes.push(child);
        }
    });

    if (optionNodes.length === 0) {
        return null;
    }

    const markedOptions = getMarkedOptionsInQuestion(questionInfo.questionNode, questionInfo.questionPos);
    if (markedOptions.length === 0) {
        return null;
    }

    const optionNodeById = new Map(optionNodes.map(optionNode => [optionNode.attrs.optionId, optionNode]));
    const consumedOptionIds = new Set<string>();
    const nextOptionNodes: PMNode[] = [];

    markedOptions.forEach(({ optionId, text }) => {
        const optionNode = optionNodeById.get(optionId);
        if (!optionNode) {
            return;
        }

        consumedOptionIds.add(optionId);
        if (optionNode.attrs.text === text) {
            nextOptionNodes.push(optionNode);
            return;
        }

        nextOptionNodes.push(
            optionNode.type.create(
                {
                    ...optionNode.attrs,
                    text,
                },
                optionNode.content,
                optionNode.marks,
            ),
        );
    });

    optionNodes.forEach(optionNode => {
        if (consumedOptionIds.has(optionNode.attrs.optionId)) {
            return;
        }

        nextOptionNodes.push(optionNode);
    });

    const hasOrderOrTextChange =
        optionNodes.length === nextOptionNodes.length &&
        optionNodes.some(
            (optionNode, index) =>
                optionNode.attrs.optionId !== nextOptionNodes[index]?.attrs.optionId ||
                optionNode.attrs.text !== nextOptionNodes[index]?.attrs.text,
        );

    if (!hasOrderOrTextChange) {
        return null;
    }

    return detectAnswerNode.type.create(detectAnswerNode.attrs, [questionInfo.questionNode, ...nextOptionNodes], detectAnswerNode.marks);
}

export const DetectAnswerNodeHeadless = Node.create({
    name: 'detectAnswer',

    group: 'block',

    content: 'detectAnswerQuestion detectAnswerOption*',

    defining: true,

    isolating: true,

    selectable: true,

    draggable: true,

    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: element => element.getAttribute('data-da-id'),
                renderHTML: attributes => ({
                    'data-da-id': attributes.id,
                }),
            },
            title: {
                default: 'Detect Answer Question',
                parseHTML: element => element.getAttribute('data-title') || 'Detect Answer Question',
                renderHTML: attributes => ({
                    'data-title': attributes.title,
                }),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="detect-answer"]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-type': 'detect-answer',
                class: 'detect-answer-container',
            }),
            0,
        ];
    },

    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: detectAnswerOptionSyncPluginKey,

                appendTransaction(transactions, _oldState, newState) {
                    if (!transactions.some(tr => tr.docChanged)) {
                        return null;
                    }

                    if (transactions.some(tr => tr.getMeta(detectAnswerOptionSyncPluginKey))) {
                        return null;
                    }

                    const tr = newState.tr;
                    let changed = false;

                    newState.doc.descendants((node, pos) => {
                        if (node.type.name !== 'detectAnswer') {
                            return;
                        }

                        const mappedPos = tr.mapping.map(pos);
                        const currentNode = tr.doc.nodeAt(mappedPos);
                        if (!currentNode || currentNode.type.name !== 'detectAnswer') {
                            return false;
                        }

                        const syncedNode = getSyncedDetectAnswerNode(currentNode, mappedPos);
                        if (!syncedNode) {
                            return false;
                        }

                        tr.replaceWith(mappedPos, mappedPos + currentNode.nodeSize, syncedNode);
                        changed = true;
                        return false;
                    });

                    if (!changed) {
                        return null;
                    }

                    tr.setMeta(detectAnswerOptionSyncPluginKey, true);
                    return tr;
                },
            }),
        ];
    },

    addCommands() {
        return {
            insertDetectAnswer:
                (options: InsertDetectAnswerOptions = {}) =>
                ({ commands }) => {
                    const instructionNode: JSONContent = {
                        type: 'paragraph',
                        content: options.instruction ? [{ type: 'text', text: options.instruction }] : undefined,
                        attrs: {
                            textAlign: null,
                        },
                    };

                    const questionSet = {
                        type: 'questionSet',
                        attrs: {
                            activityType: 'participation',
                            questionType: 'detectAnswer',
                        },
                        content: [instructionNode, defaultDetectAnswerNode(options)],
                    };

                    return commands.insertContent(questionSet);
                },
            insertBareDetectAnswer:
                (options: InsertDetectAnswerOptions = {}) =>
                ({ commands }) => {
                    return commands.insertContent(defaultDetectAnswerNode(options));
                },
            toggleDetectAnswerOptionFromSelection:
                (questionPos: number) =>
                ({ state, dispatch }) => {
                    const { from, to } = state.selection;
                    if (from === to) {
                        return false;
                    }

                    if (questionPos < 0) {
                        return false;
                    }

                    const questionNode = state.doc.nodeAt(questionPos);
                    if (!questionNode || questionNode.type.name !== 'detectAnswerQuestion') {
                        return false;
                    }

                    const questionEnd = questionPos + questionNode.nodeSize;
                    if (from < questionPos || to > questionEnd) {
                        return false;
                    }

                    const detectAnswerPos = findAncestorNodePosition(state, questionPos + 1, 'detectAnswer');
                    if (detectAnswerPos < 0) {
                        return false;
                    }

                    const detectAnswerNode = state.doc.nodeAt(detectAnswerPos);
                    if (!detectAnswerNode) {
                        return false;
                    }

                    const selectedText = state.doc.textBetween(from, to, '\n').trim();
                    if (!selectedText) {
                        return false;
                    }

                    const existingIds = getDetectAnswerMarkIdsInRange(state, from, to);
                    const detectMarkType = state.schema.marks.detectAnswerOptionMark;
                    if (!detectMarkType) {
                        return false;
                    }

                    const tr = state.tr;

                    if (existingIds.size === 1) {
                        const existingId = Array.from(existingIds)[0];
                        const changed = removeDetectAnswerOptionFromQuestion(
                            tr,
                            state,
                            existingId,
                            detectAnswerNode,
                            detectAnswerPos,
                            detectMarkType,
                        );

                        if (changed && dispatch) {
                            dispatch(tr);
                        }
                        return changed;
                    }

                    const optionId = createOptionId();
                    tr.addMark(from, to, detectMarkType.create({ optionId }));

                    const newPos = detectAnswerPos + detectAnswerNode.nodeSize - 1;
                    tr.insert(
                        newPos,
                        state.schema.nodes.detectAnswerOption.create(
                            {
                                optionId,
                                text: selectedText,
                                isCorrect: false,
                            },
                            [state.schema.nodes.detectAnswerExplanation.create({}, [state.schema.nodes.paragraph.create()])],
                        ),
                    );

                    if (dispatch) {
                        dispatch(tr);
                    }
                    return true;
                },
            removeDetectAnswerOption:
                (optionId: string) =>
                ({ state, dispatch }) => {
                    const detectMarkType = state.schema.marks.detectAnswerOptionMark;
                    if (!detectMarkType) {
                        return false;
                    }

                    const tr = state.tr;
                    let changed = false;

                    state.doc.descendants((node: PMNode, pos: number) => {
                        if (!node.isText || !node.marks?.length) {
                            return;
                        }

                        const hasMark = node.marks.some(
                            mark => mark.type.name === 'detectAnswerOptionMark' && mark.attrs.optionId === optionId,
                        );
                        if (!hasMark) {
                            return;
                        }

                        tr.removeMark(pos, pos + node.nodeSize, detectMarkType);
                        changed = true;
                    });

                    if (changed && dispatch) {
                        dispatch(tr);
                    }

                    return changed;
                },
        };
    },
});

export const DetectAnswerQuestionNodeHeadless = Node.create({
    name: 'detectAnswerQuestion',

    group: 'detectAnswerContent',

    content: function () {
        return getBasicContentSchema();
    },

    defining: true,

    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: element => element.getAttribute('data-da-question-id'),
                renderHTML: attributes => ({
                    'data-da-question-id': attributes.id,
                }),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="detect-answer-question"]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-type': 'detect-answer-question',
            }),
            0,
        ];
    },
});

export const DetectAnswerOptionNodeHeadless = Node.create({
    name: 'detectAnswerOption',

    group: 'detectAnswerContent',

    content: 'detectAnswerExplanation{1}',

    defining: true,

    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: element => element.getAttribute('data-da-option-id'),
                renderHTML: attributes => ({
                    'data-da-option-id': attributes.id,
                }),
            },
            optionId: {
                default: '',
                parseHTML: element => element.getAttribute('data-option-id') || '',
                renderHTML: attributes => ({
                    'data-option-id': attributes.optionId || '',
                }),
            },
            text: {
                default: '',
                parseHTML: element => element.getAttribute('data-option-text') || '',
                renderHTML: attributes => ({
                    'data-option-text': attributes.text || '',
                }),
            },
            isCorrect: {
                default: false,
                parseHTML: element => element.getAttribute('data-correct') === 'true',
                renderHTML: attributes => ({
                    'data-correct': attributes.isCorrect ? 'true' : 'false',
                }),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="detect-answer-option"]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-type': 'detect-answer-option',
            }),
            0,
        ];
    },
});

export const DetectAnswerExplanationNodeHeadless = Node.create({
    name: 'detectAnswerExplanation',

    group: 'detectAnswerContent',

    content: function () {
        return getBasicContentSchema();
    },

    defining: true,

    parseHTML() {
        return [
            {
                tag: 'div[data-type="detect-answer-explanation"]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-type': 'detect-answer-explanation',
            }),
            0,
        ];
    },
});

export const DetectAnswerOptionMark = Mark.create({
    name: 'detectAnswerOptionMark',

    inclusive: false,

    addAttributes() {
        return {
            optionId: {
                default: '',
                parseHTML: element => element.getAttribute('data-option-id') || '',
                renderHTML: attributes => ({
                    'data-option-id': attributes.optionId || '',
                }),
            },
        };
    },

    parseHTML() {
        return [{ tag: 'span[data-type="detect-answer-option-mark"]' }];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'span',
            mergeAttributes(HTMLAttributes, {
                'data-type': 'detect-answer-option-mark',
                class: 'rounded border border-blue-400 bg-blue-50 px-1 dark:border-blue-500 dark:bg-blue-950/30',
            }),
            0,
        ];
    },
});

export const DetectAnswerExplanationNode = DetectAnswerExplanationNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(DetectAnswerExplanationNodeView);
    },
});

export const DetectAnswerOptionNode = DetectAnswerOptionNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(DetectAnswerOptionNodeView);
    },
});

export const DetectAnswerQuestionNode = DetectAnswerQuestionNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(DetectAnswerQuestionNodeView);
    },
});

export const DetectAnswerNode = DetectAnswerNodeHeadless;
